import { motion } from "framer-motion";
import { type ReactNode } from "react";
import { MicroParticles } from "./decor";

export function ScreenShell({ children }: { children: ReactNode }) {
  return (
    <motion.div
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -40, opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="relative min-h-screen w-full overflow-hidden"
    >
      <BackgroundFX />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

export function BackgroundFX() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage:"radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize:"48px 48px" }} />
      <motion.div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full"
        style={{ background:"#8B5CF6", filter:"blur(120px)", opacity:0.28 }}
        animate={{ x:[0,40,0], y:[0,30,0] }}
        transition={{ duration:18, repeat:Infinity, ease:"easeInOut" }} />
      <motion.div className="absolute -top-24 -right-32 h-[440px] w-[440px] rounded-full"
        style={{ background:"#e879f9", filter:"blur(120px)", opacity:0.22 }}
        animate={{ x:[0,-30,0], y:[0,40,0] }}
        transition={{ duration:22, repeat:Infinity, ease:"easeInOut" }} />
      <motion.div className="absolute -bottom-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full"
        style={{ background:"#22D3EE", filter:"blur(120px)", opacity:0.2 }}
        animate={{ x:[-20,20,-20], y:[0,-20,0] }}
        transition={{ duration:20, repeat:Infinity, ease:"easeInOut" }} />
      <MicroParticles />
    </div>
  );
}

export function GradientText({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span className={`gradient-sweep ${className}`}
      style={{
        backgroundImage:"linear-gradient(90deg, #e879f9, #a78bfa, #f8fafc, #22d3ee)",
        backgroundSize:"200% auto",
        WebkitBackgroundClip:"text", backgroundClip:"text",
        WebkitTextFillColor:"transparent", color:"transparent",
      }}>
      {children}
    </span>
  );
}

export function GlassCard({ children, className = "", style, animatedBorder = false }: {
  children: ReactNode; className?: string; style?: React.CSSProperties; animatedBorder?: boolean;
}) {
  return (
    <div className={`rounded-2xl border backdrop-blur-xl ${animatedBorder ? "animated-border" : ""} ${className}`}
      style={{ background:"rgba(255,255,255,0.04)", borderColor:animatedBorder?"transparent":"rgba(255,255,255,0.08)", ...style }}>
      {children}
    </div>
  );
}

/** SimHeader — shared top bar for all answer/outcome/coaching screens.
 *  `xp` and `timerDisplay` are optional, threaded from SimPlayerPage. */
export function SimHeader({ decision = 3, xp = 0, timerDisplay }: {
  decision?: number; xp?: number; timerDisplay?: string;
}) {
  return (
    <header className="flex items-center justify-between px-6 py-5 md:px-10">
      <div className="flex items-center gap-3">
        <div className="h-6 w-6 rounded-full"
          style={{ background:"linear-gradient(135deg, #8B5CF6, #e879f9)", boxShadow:"0 0 16px rgba(139,92,246,0.6)" }} />
        <GradientText className="text-[13px] font-bold uppercase tracking-[0.2em]">Simulation</GradientText>
      </div>
      <div className="flex items-center gap-4">
        {timerDisplay && (
          <span className="rounded-full px-3 py-1 text-[12px] font-bold text-white/70"
            style={{ background:"rgba(7,9,28,0.6)", border:"1px solid rgba(255,255,255,0.15)" }}>
            ⏱ {timerDisplay}
          </span>
        )}
        <div className="rounded-full px-3 py-1 text-[12px] font-bold"
          style={{
            background:"rgba(7,9,28,0.6)", border:"1px solid transparent",
            backgroundImage:"linear-gradient(#07091C,#07091C), linear-gradient(90deg, #22D3EE, #e879f9)",
            backgroundOrigin:"border-box", backgroundClip:"padding-box, border-box", color:"#F1F5F9",
          }}>
          {xp} XP
        </div>
        <span className="text-[12px] text-white/50">Decision {decision}</span>
      </div>
    </header>
  );
}

export function ShimmerButton({ children, onClick, className = "", variant = "warm" }: {
  children: ReactNode; onClick?: () => void; className?: string; variant?: "warm" | "cool";
}) {
  return (
    <button onClick={onClick}
      className={`relative inline-flex items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-2xl py-4 px-6 text-[15px] font-bold text-white ${variant === "warm" ? "cta-gradient-warm" : "cta-gradient"} ${className}`}>
      <span className="relative z-10 inline-flex items-center gap-2 whitespace-nowrap">{children}</span>
      <span className="pointer-events-none absolute inset-0"
        style={{
          background:"linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)",
          backgroundSize:"200% 100%", animation:"shimmer 2.4s linear infinite",
        }} />
    </button>
  );
}
